"use client";

import React, { useEffect, useRef, useState } from "react";
import { MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const locations = [{
  id: 1,
  name: "MIAMI",
  address: "Serving the heart of Miami with comprehensive property inspections.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_miami_skyline.png",
  link: "/home-inspection-miami-fl/"
}, {
  id: 2,
  name: "HOMESTEAD",
  address: "Expert residential inspections for the Homestead community.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_homestead_residential.png",
  link: "/home-inspection-homestead-fl/"
}, {
  id: 3,
  name: "KENDALL",
  address: "Professional inspection services tailored for Kendall homeowners.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_kendall_suburbs.png",
  link: "/home-inspection-kendall-fl/"
}, {
  id: 4,
  name: "MIAMI-DADE",
  address: "Full-county coverage for all your insurance and buying needs.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_miami_dade_county.png",
  link: "/home-inspection-miami-dade-county/"
}, {
  id: 5,
  name: "BROWARD",
  address: "Trusted home inspections throughout Broward County.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_broward_county.png",
  link: "/home-inspection-broward-county-fl/"
}, {
  id: 6,
  name: "FLORIDA KEYS",
  address: "Specialized coastal property inspections across the Keys.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1579221/img/location_florida_keys_coastal.png",
  link: "/home-inspection-florida-keys/"
}];
export default function OurLocations() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll();
  return <section data-section-id="85225" id="location-overview" className="bg-background py-16 md:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div ref={headerRef} className={`transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
          <h2 className="text-4xl font-bold tracking-tight text-secondary mb-6">
            Location Overview
          </h2>
          <Separator className="mb-12 bg-primary h-px w-full" />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {locations.map((loc, i) => <LocationCard key={loc.id} location={loc} index={i} />)}
        </div>
      </div>
    </section>;
}
function LocationCard({
  location,
  index
}: {
  location: (typeof locations)[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <div ref={ref} data-index={index} className={`transition-all duration-700 ease-out h-full ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`} style={{
    transitionDelay: `${index * 150}ms`
  }}>
      <Card className="h-full overflow-hidden border-border/50 shadow-sm hover:shadow-lg transition-shadow duration-300 bg-card flex flex-col rounded-xl">
        <div className="relative h-64 w-full shrink-0">
          <img src={location.image} data-wvc-srcset={`${location.image}-wvc-srcset`} data-wvc-sizes={`${location.image}-wvc-sizes`} alt={location.name} className="object-cover w-full h-full" loading="lazy" decoding="async" />
          <div className="absolute inset-0 bg-secondary/20 transition-opacity duration-300 hover:bg-secondary/10" />
        </div>
        <CardContent className="px-6 pb-6 pt-6 flex-grow flex flex-col items-start text-left">
          <h3 className="text-2xl font-bold text-secondary mb-3 tracking-tight uppercase border-l-2 border-primary pl-4">
            {location.name}
          </h3>
          <p className="text-muted-foreground mb-8 flex-grow text-base font-normal">
            {location.address}
          </p>
          <Button asChild className="w-full bg-secondary text-primary hover:bg-secondary/90 rounded-none h-12 text-base font-medium shadow-none transition-colors duration-200">
            <Link to={location.link}>
              <MapPin className="mr-2 h-5 w-5 text-primary" />
              Get Directions
            </Link>
          </Button>
        </CardContent>
      </Card>
    </div>;
}